// FILE: src/pages/ReconciliationSettingsPage.jsx
//
// Reconciliation Notification Settings — UI redesign.
//
// Presentation-only rewrite in the app design language (Geist / Geist Mono,
// scoped CSS under `.rspg-root`, sticky page-top, white cards, 34px buttons).
//
// Behavior preserved byte-for-byte from the previous version:
//   - GET /accounts (no params) → res.data.accounts || []
//   - PUT /accounts/:id/settings  body { reconciliationSettings: settings }
//     success message reads res.data.account.accountName, then refetch.
//   - Display defaults when reconciliationSettings is unset:
//     Manager 1 / Accountant 15 / Admin 30 days.
//   - The in-place settings mutation + setAccounts(map) update approach.
//   - Back button → navigate(-1).
// Presentation-level change: the inline success <Alert> is now a bottom-center
// Snackbar; the error banner is a dismissible danger-soft strip.

import React, { useState, useEffect } from "react";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import API from "../api";
import { useNavigate } from "react-router-dom";

// ----------------------------------------
// CSS — fully scoped under `.rspg-root`.
// ----------------------------------------
const RECON_SETTINGS_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@400;450;500;600;700&family=Geist+Mono:wght@400;500;600&display=swap');

.rspg-root {
  --rspg-primary: #1E90FF;
  --rspg-primary-soft: #E8F3FF;
  --rspg-warn: #FFC107;
  --rspg-warn-soft: #FFF7DC;
  --rspg-warn-deep: #B86E00;
  --rspg-success: #2E7D32;
  --rspg-success-soft: #E8F5E9;
  --rspg-danger: #C62828;
  --rspg-danger-soft: #FDECEC;
  --rspg-text-1: #111827;
  --rspg-text-2: #374151;
  --rspg-text-3: #6B7280;
  --rspg-text-4: #9CA3AF;
  --rspg-border-1: #ECECEC;
  --rspg-border-2: #DDDDDD;
  --rspg-surface: #FFFFFF;
  --rspg-surface-sub: #FAFAFA;
  --rspg-surface-sub-2: #F4F4F5;
  --rspg-radius-sm: 6px;
  --rspg-radius-md: 8px;
  --rspg-radius-lg: 12px;
  --rspg-shadow-sm: 0 1px 2px rgba(15, 23, 42, 0.04);
  --rspg-shadow-md: 0 4px 16px rgba(15, 23, 42, 0.06);
  --rspg-navbar-h: 60px;

  font-family: 'Geist', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-feature-settings: 'cv11', 'ss03';
  color: var(--rspg-text-1);
  background: var(--rspg-surface-sub);
  min-height: 100vh;
  padding: 0;
  font-size: 13.5px;
  line-height: 1.5;
  letter-spacing: -0.005em;
}
.rspg-root * { box-sizing: border-box; }

/* ----- Sticky page-top ----- */
.rspg-sticky-top {
  position: sticky;
  top: var(--rspg-navbar-h);
  z-index: 30;
  background: var(--rspg-surface-sub);
  border-bottom: 1px solid var(--rspg-border-1);
}
.rspg-header {
  padding: 20px 24px 14px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
}
.rspg-title-block { min-width: 0; flex: 1 1 320px; }
.rspg-title {
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.02em;
  margin: 0 0 4px;
  color: var(--rspg-text-1);
}
.rspg-subtitle {
  display: flex; align-items: center; gap: 10px;
  font-size: 12.5px; color: var(--rspg-text-3); flex-wrap: wrap;
}
.rspg-subtitle-count {
  display: inline-flex; align-items: center; gap: 6px;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 11px; color: var(--rspg-text-3);
  background: var(--rspg-surface-sub-2);
  border: 1px solid var(--rspg-border-1);
  padding: 2px 8px; border-radius: 999px;
}
.rspg-actions { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }

/* ----- Buttons ----- */
.rspg-btn {
  display: inline-flex; align-items: center; gap: 7px;
  height: 34px; padding: 0 14px;
  font-family: inherit; font-size: 12.5px; font-weight: 500;
  border-radius: var(--rspg-radius-md);
  border: 1px solid transparent; cursor: pointer;
  transition: all 0.15s ease;
  white-space: nowrap;
}
.rspg-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.rspg-btn-primary { background: var(--rspg-primary); color: #fff; }
.rspg-btn-primary:hover:not(:disabled) { background: #1A7DD9; }
.rspg-btn-ghost {
  background: var(--rspg-surface); color: var(--rspg-text-2);
  border-color: var(--rspg-border-2);
}
.rspg-btn-ghost:hover:not(:disabled) { background: var(--rspg-surface-sub-2); }

/* ----- Body ----- */
.rspg-body { padding: 18px 24px 32px; max-width: 980px; }

.rspg-alert {
  margin-bottom: 14px;
  padding: 10px 14px;
  background: var(--rspg-danger-soft);
  color: var(--rspg-danger);
  border-left: 3px solid var(--rspg-danger);
  border-radius: var(--rspg-radius-sm);
  font-size: 13px;
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
}
.rspg-alert button {
  background: none; border: none; color: inherit; cursor: pointer;
  font-size: 16px; line-height: 1; padding: 0 4px;
}

/* ----- Legend strip (explains the three frequencies) ----- */
.rspg-legend {
  display: flex; align-items: center; gap: 14px; flex-wrap: wrap;
  margin-bottom: 14px;
  padding: 10px 14px;
  background: var(--rspg-surface);
  border: 1px solid var(--rspg-border-1);
  border-radius: var(--rspg-radius-md);
  box-shadow: var(--rspg-shadow-sm);
  font-size: 12px; color: var(--rspg-text-3);
}
.rspg-legend-item {
  display: inline-flex; align-items: center; gap: 6px;
  color: var(--rspg-text-2);
}
.rspg-legend-item b {
  font-weight: 600; color: var(--rspg-text-1);
}
.rspg-legend-item .mono {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 11.5px; color: var(--rspg-text-3);
}
.rspg-role-dot { width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; }
.rspg-role-dot.is-manager { background: var(--rspg-primary); }
.rspg-role-dot.is-accountant { background: var(--rspg-warn-deep); }
.rspg-role-dot.is-admin { background: var(--rspg-success); }

/* ----- Account cards ----- */
.rspg-cards { display: flex; flex-direction: column; gap: 10px; }
.rspg-card {
  background: var(--rspg-surface);
  border: 1px solid var(--rspg-border-1);
  border-radius: var(--rspg-radius-lg);
  box-shadow: var(--rspg-shadow-sm);
  padding: 14px 16px;
  display: flex; align-items: flex-end; gap: 18px; flex-wrap: wrap;
  transition: box-shadow 0.15s ease;
}
.rspg-card:hover { box-shadow: var(--rspg-shadow-md); }
.rspg-card-acc {
  flex: 1 1 180px; min-width: 160px;
  display: flex; flex-direction: column; gap: 2px;
  align-self: center;
}
.rspg-acc-name { font-weight: 600; font-size: 13.5px; color: var(--rspg-text-1); }
.rspg-acc-no {
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 11px; color: var(--rspg-text-3);
}
.rspg-card-fields {
  display: flex; align-items: flex-end; gap: 12px; flex-wrap: wrap;
}
.rspg-field { display: flex; flex-direction: column; gap: 4px; }
.rspg-field-lab {
  display: inline-flex; align-items: center; gap: 5px;
  font-size: 10.5px; font-weight: 500; text-transform: uppercase;
  letter-spacing: 0.04em; color: var(--rspg-text-3);
}
.rspg-days-input {
  display: flex; align-items: center;
  height: 34px;
  border: 1px solid var(--rspg-border-2);
  border-radius: var(--rspg-radius-md);
  background: var(--rspg-surface);
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.rspg-days-input:focus-within {
  border-color: var(--rspg-primary);
  box-shadow: 0 0 0 3px var(--rspg-primary-soft);
}
.rspg-days-input input {
  width: 64px; height: 100%;
  padding: 0 4px 0 10px;
  border: none; outline: none;
  background: transparent;
  font-family: 'Geist Mono', monospace;
  font-variant-numeric: tabular-nums;
  font-size: 13px; color: var(--rspg-text-1);
  text-align: right;
  -moz-appearance: textfield;
}
.rspg-days-input input::-webkit-outer-spin-button,
.rspg-days-input input::-webkit-inner-spin-button {
  -webkit-appearance: none; margin: 0;
}
.rspg-days-unit {
  padding: 0 9px 0 5px;
  font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--rspg-text-4);
  border-left: 1px solid var(--rspg-border-1);
  height: 100%;
  display: inline-flex; align-items: center;
  background: var(--rspg-surface-sub);
}
.rspg-card-save { margin-left: auto; align-self: center; }
@media (max-width: 720px) {
  .rspg-card { align-items: stretch; flex-direction: column; }
  .rspg-card-save { margin-left: 0; align-self: flex-start; }
}

/* ----- Loading / empty states ----- */
.rspg-state {
  padding: 56px 32px;
  text-align: center;
  color: var(--rspg-text-3);
  background: var(--rspg-surface);
  border: 1px solid var(--rspg-border-1);
  border-radius: var(--rspg-radius-lg);
  box-shadow: var(--rspg-shadow-sm);
}
.rspg-state-icon {
  margin: 0 auto 12px;
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  border-radius: 50%;
  background: var(--rspg-surface-sub-2);
  color: var(--rspg-text-3);
}
.rspg-state-title { color: var(--rspg-text-2); font-size: 14px; font-weight: 500; margin-bottom: 4px; }
.rspg-state-sub { font-size: 12.5px; color: var(--rspg-text-3); max-width: 380px; margin: 0 auto; }
`;

// ----------------------------------------
// Small inline icons
// ----------------------------------------
function IconBack({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M9.5 3.5L5 8l4.5 4.5"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function IconSave({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3 13.5h10a.5.5 0 00.5-.5V5.2a.5.5 0 00-.15-.36l-2.2-2.19a.5.5 0 00-.35-.15H3a.5.5 0 00-.5.5v10a.5.5 0 00.5.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M5.5 2.5v3h5v-3M5.5 13.5V9h5v4.5" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}
function IconBell({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" aria-hidden>
      <path
        d="M10 2.5a4.5 4.5 0 00-4.5 4.5v2.6c0 .5-.2 1-.55 1.36L3.6 12.4c-.5.52-.13 1.6.6 1.6h11.6c.73 0 1.1-1.08.6-1.6l-1.35-1.44A1.96 1.96 0 0114.5 9.6V7A4.5 4.5 0 0010 2.5z"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
      <path d="M8.2 16.5a2 2 0 003.6 0" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

// Numeric input helper — stops accidental scroll-wheel edits.
const dayInputProps = {
  type: "number",
  onWheel: (e) => e.target.blur(),
};

const ROLE_FIELDS = [
  { key: "Manager", dotClass: "is-manager", defaultDays: 1 },
  { key: "Accountant", dotClass: "is-accountant", defaultDays: 15 },
  { key: "Admin", dotClass: "is-admin", defaultDays: 30 },
];

export default function ReconciliationSettingsPage() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchAccounts() {
    try {
      setLoading(true);
      // GET all accounts; using the endpoint that returns reconciliationSettings and notifications
      const res = await API.get("/accounts");
      // Adjust based on your API response structure; here we assume { accounts, total, page }
      setAccounts(res.data.accounts || []);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching accounts:", err);
      setError("Failed to fetch accounts.");
      setLoading(false);
    }
  }

  async function handleSave(accountId, settings) {
    try {
      const res = await API.put(`/accounts/${accountId}/settings`, { reconciliationSettings: settings });
      setSuccessMsg(`Settings updated for account "${res.data.account.accountName}".`);
      // Optionally refresh accounts list
      fetchAccounts();
    } catch (err) {
      console.error("Error updating settings:", err);
      setError("Failed to update settings.");
    }
  }

  return (
    <div className="rspg-root">
      <style>{RECON_SETTINGS_CSS}</style>

      {/* Sticky page-top */}
      <div className="rspg-sticky-top">
        <div className="rspg-header">
          <div className="rspg-title-block">
            <h1 className="rspg-title">Reconciliation Notification Settings</h1>
            <div className="rspg-subtitle">
              <span>
                How often each role is reminded to reconcile an account, in days.
              </span>
              {!loading && (
                <span className="rspg-subtitle-count">
                  {accounts.length} {accounts.length === 1 ? "account" : "accounts"}
                </span>
              )}
            </div>
          </div>

          <div className="rspg-actions">
            <button
              type="button"
              className="rspg-btn rspg-btn-ghost"
              onClick={() => navigate(-1)}
            >
              <IconBack /> Back
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="rspg-body">
        {error && (
          <div className="rspg-alert" role="alert">
            <span>{error}</span>
            <button type="button" aria-label="Dismiss error" onClick={() => setError("")}>
              &times;
            </button>
          </div>
        )}

        {/* Legend — default frequency per role when nothing is saved yet */}
        <div className="rspg-legend">
          <span>Defaults when unset:</span>
          <span className="rspg-legend-item">
            <span className="rspg-role-dot is-manager" />
            <b>Manager</b> <span className="mono">1d</span>
          </span>
          <span className="rspg-legend-item">
            <span className="rspg-role-dot is-accountant" />
            <b>Accountant</b> <span className="mono">15d</span>
          </span>
          <span className="rspg-legend-item">
            <span className="rspg-role-dot is-admin" />
            <b>Admin</b> <span className="mono">30d</span>
          </span>
        </div>

        {loading ? (
          <div className="rspg-state">
            <CircularProgress size={28} />
            <div className="rspg-state-title" style={{ marginTop: 12 }}>
              Loading accounts…
            </div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="rspg-state">
            <div className="rspg-state-icon">
              <IconBell />
            </div>
            <div className="rspg-state-title">No accounts found</div>
            <div className="rspg-state-sub">
              Once accounts exist for your organization, their reconciliation
              reminder frequencies can be configured here.
            </div>
          </div>
        ) : (
          <div className="rspg-cards">
            {accounts.map((account) => {
              // Use existing settings or defaults if not set
              const settings = account.reconciliationSettings || {
                Manager: { frequencyDays: 1 },
                Accountant: { frequencyDays: 15 },
                Admin: { frequencyDays: 30 },
              };
              // Use local state per row? For simplicity, we update in place.
              return (
                <div className="rspg-card" key={account._id}>
                  <div className="rspg-card-acc">
                    <span className="rspg-acc-name">{account.accountName}</span>
                    {account.accountNo && (
                      <span className="rspg-acc-no">{account.accountNo}</span>
                    )}
                  </div>

                  <div className="rspg-card-fields">
                    {ROLE_FIELDS.map((role) => (
                      <div className="rspg-field" key={role.key}>
                        <label
                          className="rspg-field-lab"
                          htmlFor={`rspg-${account._id}-${role.key}`}
                        >
                          <span className={`rspg-role-dot ${role.dotClass}`} />
                          {role.key}
                        </label>
                        <span className="rspg-days-input">
                          <input
                            id={`rspg-${account._id}-${role.key}`}
                            {...dayInputProps}
                            value={settings[role.key].frequencyDays}
                            onChange={(e) => {
                              const newVal = Number(e.target.value);
                              settings[role.key].frequencyDays = newVal;
                              setAccounts(prev =>
                                prev.map(acc =>
                                  acc._id === account._id ? { ...acc, reconciliationSettings: settings } : acc
                                )
                              );
                            }}
                          />
                          <span className="rspg-days-unit">days</span>
                        </span>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="rspg-btn rspg-btn-primary rspg-card-save"
                    onClick={() => handleSave(account._id, settings)}
                  >
                    <IconSave /> Save
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Success feedback */}
      <Snackbar
        open={Boolean(successMsg)}
        autoHideDuration={4000}
        onClose={() => setSuccessMsg("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSuccessMsg("")}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMsg}
        </Alert>
      </Snackbar>
    </div>
  );
}
